import React from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import InvoiceSearchBar from '../../../components/Billing/Invoice/InvoiceSearchBar';
import { ReactComponent as FilterIcon } from '../../../assets/SVG/filter.svg';
import NewInvoiceButton from '../../../components/Billing/Invoice/NewInvoiceButton';
import { FilterIconSx } from './InvoicePatientSelector.styles';
import Badge from '@mui/material/Badge';

interface InvoiceHeaderProps {
  activeCategory: string;
  onCategoryChange: (cat: { label: string }) => void;
  categories: { label: string; name:string; count: number }[];
  search: string;
  onSearchChange: (val: string) => void;
  onOpenFilter: () => void;
  onNewInvoice: () => void;
  showFilterHint?: boolean;
  showFilters?: boolean;
}

const InvoiceHeader: React.FC<InvoiceHeaderProps> = ({
  activeCategory,
  onCategoryChange,
  categories,
  search,
  onSearchChange,
  onOpenFilter,
  onNewInvoice,
  showFilterHint = false,
  showFilters = true,
}) => {
  const { t : tBilling} = useTranslation('billing_config');
  
  return (
    <Grid
      component="header"
      container
      spacing={{ xs: 1, sm: 1.5, md: 2 }}
      alignItems="stretch"
      sx={{ my: 3, width: '100%' }}
      wrap="wrap"
      role="banner"
    >
      {/* Tabs */}
      <Grid
        size={{ xs: 12, sm: 12, md: 'auto', lg: 'auto', xl: 'auto' }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 2, md: 0 },
          justifyContent: { xs: 'center', md: 'flex-start' },
          flexShrink: 0,
          overflow: 'auto',
        }}
      >
        {showFilters && (
          <InvoiceCategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={onCategoryChange}
            categories={categories}
          />
        )}
      </Grid>

      {/* Search, Filter, New Invoice */}
      <Grid
        size={{ xs: 12, sm: 12, md: 'grow', lg: 'grow', xl: 'grow' }}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'center', sm: 'flex-end', md: 'flex-end' },
          gap: { xs: 1, sm: 1.5, md: 2 },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 1, sm: 0 },
            flexShrink: 0,
          }}
        >
          {showFilters && (
            <InvoiceSearchBar value={search} onChange={onSearchChange} placeholder={tBilling('invoice.filter.search')} />
          )}
          <Badge
            color="warning"
            variant="dot"
            overlap="circular"
            invisible={!showFilterHint}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ '& .MuiBadge-badge': { top: 10, right: 10 } }}
          >
            <IconButton 
              data-testid="invoice-header-filter-open"
              aria-label={showFilterHint ? 'Open invoice filters (filters active)' : 'Open invoice filters'}
              onClick={onOpenFilter}
            >
              <FilterIcon style={FilterIconSx} />
            </IconButton>
          </Badge>
        </Box>
        <Box
          sx={{
            mx: { xs: 0, sm: 0.5, md: 1 },
            width: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            flexShrink: 0,
          }}
        >
          <NewInvoiceButton data-testid="invoice-header-new-invoice" onClick={onNewInvoice} label={tBilling('invoice.filter.new_invoice')} />
        </Box>
      </Grid>
    </Grid>
  );
};

export default InvoiceHeader;
